import MoreHoriz from "@mui/icons-material/MoreHoriz";
import { Box, IconButton, ListItemButton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";

import type { MyNotificationsQuery } from "../../generated-graphql/graphql";

import { NotificationLiveStatus } from "./NotificationLiveStatus";
import { NotificationTypeIcon } from "./NotificationTypeIcon";
import { NotificationUnreadIndicator } from "./NotificationVisualIndicators";

export type TrayNotification = MyNotificationsQuery["myNotifications"]["items"][number];

function getRelativeTime(value: string) {
  return (
    DateTime.fromISO(value, { zone: "utc" }).toRelative({ base: DateTime.now(), style: "short" }) ??
    ""
  );
}

function FacilityName({ name }: { name: string }) {
  return (
    <>
      <Typography
        variant="caption"
        color="text.disabled"
        title={name}
        sx={{
          lineHeight: 1.3,
          fontWeight: 400,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {name}
      </Typography>
      <Box
        component="span"
        sx={{
          bgcolor: "text.disabled",
          borderRadius: "50%",
          flexShrink: 0,
          height: 3,
          width: 3,
        }}
      />
    </>
  );
}

export function NotificationTrayRow({
  notificationUser,
  actionsOpen,
  onOpen,
  onActionsClick,
}: {
  notificationUser: TrayNotification;
  actionsOpen: boolean;
  onOpen: () => void;
  onActionsClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const { notification } = notificationUser;
  const isUnread = !notificationUser.readAt;

  return (
    <ListItemButton
      alignItems="flex-start"
      onClick={onOpen}
      sx={{
        bgcolor: (theme) => (isUnread ? alpha(theme.palette.primary.main, 0.035) : "transparent"),
        minHeight: 60,
        px: 2,
        py: 1.1,
        transition: "background-color 120ms ease",
        "&:focus-within .notification-row-actions": {
          opacity: 1,
        },
        "&:hover": {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.055),
        },
        "&:hover .notification-row-actions": {
          opacity: 1,
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="flex-start"
        sx={{ minWidth: 0, width: "100%" }}
      >
        <NotificationTypeIcon
          category={notification.category}
          programArea={notification.programArea ?? null}
        />
        <Stack spacing={0.25} sx={{ minWidth: 0, width: "100%" }}>
          <Typography
            variant="body1"
            title={notification.title}
            sx={{
              color: isUnread ? "text.primary" : "text.secondary",
              fontWeight: isUnread ? 700 : 500,
              lineHeight: 1.35,
              overflowWrap: "anywhere",
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
          >
            {notification.title}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
            {notification.facility?.name ? (
              <FacilityName name={notification.facility.name} />
            ) : null}
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ lineHeight: 1.3, flexShrink: 0 }}
            >
              {getRelativeTime(notification.createdAt)}
            </Typography>
            <NotificationLiveStatus liveStatus={notification.subject?.liveStatus} />
          </Stack>
        </Stack>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexShrink={0}
          sx={{ minHeight: 28, pt: 0.25 }}
        >
          {isUnread ? <NotificationUnreadIndicator unread /> : null}
          <Tooltip title="Notification actions">
            <IconButton
              aria-label={`Notification actions for ${notification.title}`}
              className="notification-row-actions"
              size="small"
              onClick={onActionsClick}
              sx={{
                color: "text.secondary",
                height: 28,
                opacity: actionsOpen ? 1 : 0,
                transition: "opacity 120ms ease",
                width: 28,
                // No hover to reveal it on a touch screen.
                "@media (hover: none)": {
                  opacity: 1,
                },
                "&:hover": {
                  bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.08),
                },
              }}
            >
              <MoreHoriz fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </ListItemButton>
  );
}
